import { DataType, Form, FormOptions, Section, useNotify } from '@globaleris/ui.library'
import { Box, Button, makeStyles, Typography, useTheme } from '@material-ui/core'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { EnumCommodity, EnumTipoPeaje } from '../../../../../../api'
import { EnumPriceComponentReference } from '../../../../../../api/Argentia/enums'
import { EnumPresionTension } from '../../../../../../api/oData/enums/enumPresionTension'
import { EnumRegionImpuestos } from '../../../../../../api/oData/enums/enumRegionImpuestos'
import { CardWithTransition } from '../../../../../../components/shared/cardWithTransition/CardWithTransition'
import { enumToSelectOptionArray } from '../../../../../../helpers'
import { getCustomerDomain } from '../../../../../../redux'
import { PriceComponentInstancesSearchFilter } from '../../models/priceComponentInstancesSearch.model'
import { searchFilterCardStyles } from './priceComponentInstancesSearchCard.styles'

interface PriceComponentInstancesSearchCardProps {
  showCard: boolean
  referenceType: EnumPriceComponentReference
  commodity: EnumCommodity
  priceComponentTypeId: number
  companyId: number
  onSearch: (filters: PriceComponentInstancesSearchFilter) => void
  onCleanFilters: () => void
}

export const PriceComponentInstancesSearchCard: React.FC<
  PriceComponentInstancesSearchCardProps
> = ({
  showCard,
  referenceType,
  commodity,
  priceComponentTypeId,
  companyId,
  onSearch,
  onCleanFilters
}) => {
  const [t] = useTranslation(['globalerisLibrary', 'priceComponentTypeInstances', 'enums'])
  const currentTheme = useTheme()
  const useStyles = makeStyles(searchFilterCardStyles(currentTheme))
  const classes = useStyles()
  const { error } = useNotify()

  const initialFormState: PriceComponentInstancesSearchFilter = {
    priceComponentTypeId,
    commodity,
    companyId,
    referenceType,
    searchText: '',
    leadIds: [],
    tollIds: [],
    presionTensionIds: [],
    regionImpuestosIds: []
  }

  const [searchFormData, setSearchFormData] =
    useState<PriceComponentInstancesSearchFilter>(initialFormState)
  const [minCharError, setMinCharError] = useState<boolean>(false)

  // Referencias de tipo
  const isPeaje = EnumPriceComponentReference.Peaje === referenceType
  const isPresionTension = EnumPriceComponentReference.PresionTension === referenceType
  const isLead = EnumPriceComponentReference.Lead === referenceType
  const isRegionImpuestos = EnumPriceComponentReference.RegionImpuestos === referenceType
  const isContrato = EnumPriceComponentReference.Contrato === referenceType
  const isFlexibilidad = EnumPriceComponentReference.Flexibilidad === referenceType

  // #region Handlers Starts

  const handleInputChange = (fieldName: string, value: any) => {
    setSearchFormData(prevState => ({
      ...prevState,
      [fieldName]: value
    }))
  }

  const handleCharValidation = (value: string) => {
    if (value !== '' && value.length < 3) {
      setMinCharError(true)
    } else {
      setMinCharError(false)
    }
  }

  const handleCleanFilters = () => {
    setSearchFormData(initialFormState)
    setMinCharError(false)
    onCleanFilters()
  }

  const isSearchDisabled = (): boolean => {
    // Si hay searchText, debe tener al menos 3 caracteres
    if (searchFormData.searchText && searchFormData.searchText.length < 3) {
      return true
    }

    // Al menos un filtro debe estar presente
    const hasSearchText = searchFormData.searchText && searchFormData.searchText.length >= 3
    const hasLeadIds = searchFormData.leadIds && searchFormData.leadIds.length > 0
    const hasTollIds = searchFormData.tollIds && searchFormData.tollIds.length > 0
    const hasPresionTensionIds =
      searchFormData.presionTensionIds && searchFormData.presionTensionIds.length > 0
    const hasRegionImpuestosIds =
      searchFormData.regionImpuestosIds && searchFormData.regionImpuestosIds.length > 0

    return !(
      hasSearchText ||
      hasLeadIds ||
      hasTollIds ||
      hasPresionTensionIds ||
      hasRegionImpuestosIds
    )
  }

  // #endregion Handlers Ends

  // #region Form Definition Starts

  const searchFormOptions: FormOptions = {
    elevation: 0,
    columns: 1,
    analytics: {
      customer: getCustomerDomain(),
      view: t('priceComponentTypeInstances:searchCard.analytics')
    }
  }

  const buildFormInputs = () => {
    const inputs: any[] = []

    // Búsqueda textual para todos excepto Peaje y PresionTension (que usan combos)
    if (!isPeaje && !isPresionTension) {
      inputs.push({
        name: 'searchText',
        label: t('priceComponentTypeInstances:components.searchCard.inputs.searchText'),
        type: DataType.TEXT,
        clearable: true,
        helpText: t('priceComponentTypeInstances:components.searchCard.helpText.minChar'),
        onChange: (value: string) => {
          handleCharValidation(value)
          handleInputChange('searchText', value)
        }
      })
    }

    if (isPeaje) {
      inputs.push({
        name: 'tollIds',
        label: t('priceComponentTypeInstances:components.searchCard.inputs.tollId'),
        type: DataType.MULTISELECT,
        selectOptions: enumToSelectOptionArray(EnumTipoPeaje, 'enums:EnumTipoPeaje'),
        clearable: true,
        onChange: (value: number[]) => handleInputChange('tollIds', value || [])
      })
    }

    if (isPresionTension) {
      inputs.push({
        name: 'presionTensionIds',
        label: t('priceComponentTypeInstances:components.searchCard.inputs.presionTensionId'),
        type: DataType.MULTISELECT,
        selectOptions: enumToSelectOptionArray(EnumPresionTension, 'enums:EnumPresionTension'),
        clearable: true,
        onChange: (value: number[]) => handleInputChange('presionTensionIds', value || [])
      })
    }

    if (isRegionImpuestos) {
      inputs.push({
        name: 'regionImpuestosIds',
        label: t('priceComponentTypeInstances:components.searchCard.inputs.regionImpuestosId'),
        type: DataType.MULTISELECT,
        selectOptions: enumToSelectOptionArray(EnumRegionImpuestos, 'enums:EnumRegionImpuestos'),
        clearable: true,
        onChange: (value: number[]) => handleInputChange('regionImpuestosIds', value || [])
      })
    }

    return inputs
  }

  const searchFormSections: Section[] = [
    {
      inputs: buildFormInputs()
    }
  ]

  // #endregion Form Definition Ends

  // #region Hooks Starts

  useEffect(() => {
    // Reset form cuando cambie el referenceType
    setSearchFormData(initialFormState)
    setMinCharError(false)
  }, [referenceType, priceComponentTypeId])

  // #endregion Hooks Ends

  return (
    <CardWithTransition show={showCard} unmountOnExit={true} className={classes.card}>
      <Box
        onKeyDown={(e: React.KeyboardEvent) => {
          if (e.key === 'Enter' && !isSearchDisabled()) {
            e.preventDefault()
            onSearch(searchFormData)
          }
        }}
      >
        <Form
          titleCustom={
            <Typography variant="h5" color="primary" className={classes.titleCustom}>
              {t('priceComponentTypeInstances:components.searchCard.title')}
            </Typography>
          }
          sections={searchFormSections}
          data={searchFormData}
          options={searchFormOptions}
        />
      </Box>
      <Box className={classes.footer}>
        <Button variant="text" color="primary" size="small" onClick={handleCleanFilters}>
          {t('priceComponentTypeInstances:components.searchCard.buttons.clean')}
        </Button>
        <Button
          variant="contained"
          color="primary"
          size="small"
          disabled={isSearchDisabled()}
          onClick={() => onSearch(searchFormData)}
        >
          {t('priceComponentTypeInstances:components.searchCard.buttons.search')}
        </Button>
      </Box>
    </CardWithTransition>
  )
}
